import { theme as antdTheme, type ThemeConfig } from 'antd';
import { NOTION_DARK } from './notion-palette.js';
import {
  accent,
  motion,
  neutral,
  radius,
  semanticDark,
  semanticLight,
  shadow,
  typography,
} from './tokens.js';

/**
 * Maps our Notion-derived design tokens onto Ant Design's theme.
 *
 * Ant Design is used for what it is genuinely excellent at — dense tables,
 * form validation, date pickers, drawers — and then restyled at the token
 * level so it reads as ours rather than as stock antd. Token overrides beat
 * CSS overrides here: they apply everywhere the component is used, including
 * inside popups rendered into a portal, and they cannot be defeated by
 * specificity.
 *
 * If a component needs a value that is not expressible as a token, that is a
 * signal to wrap it in `src/components/` rather than to write a one-off
 * override in a feature.
 */

/** Shared by light and dark: shape, type and motion do not change with mode. */
const shapeAndType = {
  fontFamily: typography.fontFamily,
  fontFamilyCode: typography.fontFamilyMono,
  fontSize: typography.size.base,
  fontSizeSM: typography.size.sm,
  fontSizeLG: typography.size.lg,
  fontSizeXL: typography.size.xl,
  fontSizeHeading1: typography.size['3xl'],
  fontSizeHeading2: typography.size['2xl'],
  fontSizeHeading3: typography.size.xl,
  fontSizeHeading4: typography.size.lg,
  fontSizeHeading5: typography.size.md,

  lineHeight: typography.lineHeight.normal,
  lineHeightHeading1: typography.lineHeight.tight,
  lineHeightHeading2: typography.lineHeight.tight,
  lineHeightHeading3: typography.lineHeight.snug,

  borderRadius: radius.base,
  borderRadiusSM: radius.sm,
  borderRadiusLG: radius.md,
  borderRadiusXS: radius.sm,

  // Antd's defaults are a touch taller than a dense operational tool wants.
  controlHeight: 36,
  controlHeightSM: 28,
  controlHeightLG: 44,

  motionDurationFast: motion.duration.fast,
  motionDurationMid: motion.duration.base,
  motionDurationSlow: motion.duration.slow,
  motionEaseInOut: motion.easing.standard,
  motionEaseOut: motion.easing.enter,

  wireframe: false,
} as const;

const lightTokens = {
  ...shapeAndType,

  // Notion blue. The only accent in the product.
  colorPrimary: accent[500],
  colorPrimaryHover: accent[400],
  colorPrimaryActive: accent[600],
  colorPrimaryBg: accent[50],
  colorPrimaryBgHover: accent[100],
  colorPrimaryBorder: accent[200],

  colorSuccess: semanticLight.success.solid,
  colorSuccessBg: semanticLight.success.bg,
  colorSuccessBorder: semanticLight.success.border,
  colorWarning: semanticLight.warning.solid,
  colorWarningBg: semanticLight.warning.bg,
  colorWarningBorder: semanticLight.warning.border,
  colorError: semanticLight.danger.solid,
  colorErrorBg: semanticLight.danger.bg,
  colorErrorBorder: semanticLight.danger.border,
  colorInfo: semanticLight.info.solid,
  colorInfoBg: semanticLight.info.bg,
  colorInfoBorder: semanticLight.info.border,

  colorTextBase: neutral[900],
  colorText: neutral[900],
  colorTextSecondary: neutral[600],
  colorTextTertiary: neutral[500],
  colorTextQuaternary: neutral[400],
  colorTextDescription: neutral[600],

  colorBgBase: neutral[0],
  colorBgContainer: neutral[0],
  // The page sits one step back from its cards, so cards read as surfaces
  // without needing a shadow to lift them. `#F7F7F5` is Notion's page chrome.
  colorBgLayout: neutral[50],
  colorBgElevated: neutral[0],
  colorBgSpotlight: neutral[850],
  colorFillSecondary: neutral[100],
  colorFillTertiary: neutral[50],
  colorFillQuaternary: neutral[25],

  colorBorder: neutral[200],
  colorBorderSecondary: neutral[150],
  colorSplit: neutral[150],

  boxShadow: shadow.base,
  boxShadowSecondary: shadow.md,
  boxShadowTertiary: shadow.sm,
} as const;

const darkTokens = {
  ...shapeAndType,

  // Notion's dark blue is a genuinely different, more vivid hue than its
  // light-mode steel blue — not a lightened version of it. Honoured here
  // rather than "corrected", because it is what makes their dark mode work.
  colorPrimary: accent.darkSolid,
  colorPrimaryHover: accent.darkText,
  colorPrimaryActive: '#2668B0',
  colorPrimaryBg: accent.darkBg,
  colorPrimaryBgHover: '#25313A',
  colorPrimaryBorder: '#2A3742',

  colorSuccess: semanticDark.success.solid,
  colorSuccessBg: semanticDark.success.bg,
  colorSuccessBorder: semanticDark.success.border,
  colorWarning: semanticDark.warning.solid,
  colorWarningBg: semanticDark.warning.bg,
  colorWarningBorder: semanticDark.warning.border,
  colorError: semanticDark.danger.solid,
  colorErrorBg: semanticDark.danger.bg,
  colorErrorBorder: semanticDark.danger.border,
  colorInfo: semanticDark.info.solid,
  colorInfoBg: semanticDark.info.bg,
  colorInfoBorder: semanticDark.info.border,

  colorTextBase: NOTION_DARK.text.default,
  colorText: NOTION_DARK.text.default,
  colorTextSecondary: NOTION_DARK.text.gray,
  colorTextTertiary: NOTION_DARK.icon.gray,
  colorTextQuaternary: '#6B6B6B',
  colorTextDescription: NOTION_DARK.text.gray,

  colorBgBase: neutral[1000],
  // In dark mode the relationship inverts: the page is the darkest surface and
  // containers step *forward*. Reusing the light-mode relationship makes cards
  // look like holes punched in the page. These are Notion's own dark surfaces:
  // #191919 page, #202020 raised, #252525 elevated.
  colorBgContainer: neutral[975],
  colorBgLayout: neutral[1000],
  colorBgElevated: neutral[950],
  colorBgSpotlight: '#2C2C2C',
  colorFillSecondary: '#2C2C2C',
  colorFillTertiary: '#242424',
  colorFillQuaternary: '#1F1F1F',

  colorBorder: '#2F2F2F',
  colorBorderSecondary: '#272727',
  colorSplit: '#272727',

  boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.5)',
  boxShadowSecondary: '0 4px 12px -2px rgba(0, 0, 0, 0.55)',
  boxShadowTertiary: '0 1px 2px 0 rgba(0, 0, 0, 0.4)',
} as const;

/**
 * Per-component overrides, for the places where a global token cannot express
 * the intent.
 *
 * Anything mode-dependent is written as a CSS variable rather than a hex, so
 * one object serves both themes.
 */
const components: ThemeConfig['components'] = {
  /**
   * An alert with a description, at the spacing the rest of a form uses
   * (orbit-ui#81).
   *
   * Ant Design gives these `paddingMD paddingContentHorizontalLG` — 16px by
   * 24px — and a 24px icon. Beside a form laid out on a 12px rhythm the
   * "Declaration on file" notice came out taller than the two fields under
   * it, and read as a different design rather than a roomy one.
   *
   * **Set as a component token, not as CSS.** The first attempt was a rule
   * in `global.css`, and it never applied: antd emits
   * `.ant-alert.ant-alert-with-description` and the override was a single
   * class, so it lost on specificity every time. Tokens are the supported
   * way in and cannot be out-specified.
   */
  Alert: {
    withDescriptionPadding: '12px 16px',
    withDescriptionIconSize: 20,
  },
  Layout: {
    bodyBg: 'transparent',
    headerBg: 'transparent',
    siderBg: 'transparent',
    headerPadding: 0,
  },
  Menu: {
    // The sidebar is navigation, not a card. No borders, no fill, no shadow —
    // the selected item is the only thing carrying any weight.
    itemBg: 'transparent',
    subMenuItemBg: 'transparent',
    itemBorderRadius: radius.base,
    itemMarginInline: 6,
    itemMarginBlock: 2,
    itemHeight: 36,
    iconMarginInlineEnd: 10,
    collapsedIconSize: 18,
  },
  Table: {
    headerBg: 'transparent',
    headerSplitColor: 'transparent',
    cellPaddingBlock: 10,
    cellPaddingInline: 14,
    borderRadius: 0,
    headerBorderRadius: 0,
  },
  Card: {
    // Hairline, not shadow.
    boxShadow: 'none',
    boxShadowTertiary: 'none',
  },
  Button: {
    fontWeight: typography.weight.medium,
    primaryShadow: 'none',
    defaultShadow: 'none',
    dangerShadow: 'none',
    paddingInline: 14,
  },
  Input: { paddingBlock: 6, paddingInline: 11 },
  Select: { optionSelectedFontWeight: typography.weight.medium },
  Tag: {
    defaultBg: 'transparent',
    borderRadiusSM: radius.sm,
  },
  Modal: { titleFontSize: typography.size.lg },
  Drawer: { footerPaddingBlock: 12, footerPaddingInline: 20 },
  Tabs: { horizontalItemGutter: 24, titleFontSize: typography.size.base },
  Tooltip: { borderRadius: radius.base },
  Form: { verticalLabelPadding: '0 0 6px' },
  Descriptions: { labelBg: 'transparent' },
};

export type ThemeMode = 'light' | 'dark';

export function buildTheme(mode: ThemeMode): ThemeConfig {
  return {
    algorithm: mode === 'dark' ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm,
    token: mode === 'dark' ? darkTokens : lightTokens,
    components,
    // CSS variables, with the prefix pinned. Our own components reference
    // `var(--ant-color-text)` and friends directly, so the prefix is part of
    // the contract between this theme and `src/components` — it must not drift
    // with an antd default.
    cssVar: { prefix: 'ant' },
    hashed: true,
  };
}

export const lightTheme = buildTheme('light');
export const darkTheme = buildTheme('dark');
